import { startTransition } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Button, ContentCard, Row } from "@narmi/design_system";
import { useACHPassthroughImportContext } from "../ACHPassthroughImportContext";
import styles from "../../transfer/ach/ReceiptScreen.module.scss";
import {
  formatCurrencyFromCents,
  formatDate,
  formatEffectiveDateRange,
} from "./passthroughFormat";
import receiptStyles from "./PassthroughReceiptScreen.module.scss";

const PassthroughReceiptScreen = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { validateResponse, submittedState, clearValidation } =
    useACHPassthroughImportContext();
  const isPendingApproval = submittedState === "pending_approval";

  if (
    !validateResponse ||
    !validateResponse.file_summary ||
    !validateResponse.submission
  ) {
    return <Navigate to="../.." replace />;
  }

  const { file_summary: fileSummary, submission: submissionPreview } =
    validateResponse;
  const effectiveDateRange = formatEffectiveDateRange(fileSummary);

  const handleDone = () => {
    // Must share a transition with navigate() (react-router wraps it in one internally),
    // or clearValidation() flushes first and the stale-validateResponse redirect above
    // fires before the URL changes.
    startTransition(() => {
      clearValidation();
      navigate("/dashboard");
    });
  };

  const handleMakeAnotherPayment = () => {
    startTransition(() => {
      clearValidation();
      navigate("../..", { state: { preselectMode: "ach_passthrough" } });
    });
  };

  return (
    <div className={styles.receiptContainer}>
      <ContentCard>
        <div className={styles.cardContainer}>
          <h2 className={`fontColor--heading ${receiptStyles.title}`}>
            {isPendingApproval
              ? l10n.getString("ach-passthrough-receipt-dual-approval-title")
              : l10n.getString("ach-passthrough-receipt-title")}
            <i className="narmi-icon-check margin--left--xs" />
          </h2>
          <div className={styles.contentContainer}>
            {fileSummary.payment_credit_count > 0 && (
              <Row>
                <Row.Item>
                  <p>{l10n.getString("ach-passthrough-review-sending")}</p>
                </Row.Item>
                <Row.Item shrink>
                  <p>
                    {l10n.getString("ach-passthrough-review-direction-detail", {
                      amount: formatCurrencyFromCents(
                        fileSummary.payment_total_credit_amount
                      ),
                      count: fileSummary.payment_credit_count,
                    })}
                  </p>
                </Row.Item>
              </Row>
            )}
            {fileSummary.payment_debit_count > 0 && (
              <Row>
                <Row.Item>
                  <p>{l10n.getString("ach-passthrough-review-collecting")}</p>
                </Row.Item>
                <Row.Item shrink>
                  <p>
                    {l10n.getString("ach-passthrough-review-direction-detail", {
                      amount: formatCurrencyFromCents(
                        fileSummary.payment_total_debit_amount
                      ),
                      count: fileSummary.payment_debit_count,
                    })}
                  </p>
                </Row.Item>
              </Row>
            )}
            <Row>
              <Row.Item>
                <p>{l10n.getString("ach-passthrough-review-processing-date")}</p>
              </Row.Item>
              <Row.Item shrink>
                <p>{formatDate(submissionPreview.processing_date)}</p>
              </Row.Item>
            </Row>
            {effectiveDateRange && (
              <Row>
                <Row.Item>
                  <p>
                    {l10n.getString("ach-passthrough-validation-check-effective-date")}
                  </p>
                </Row.Item>
                <Row.Item shrink>
                  <p>{effectiveDateRange}</p>
                </Row.Item>
              </Row>
            )}
          </div>
          <div className={receiptStyles.buttonStack}>
            <Button
              label={l10n.getString("label-done")}
              testId="passthrough-receipt-done"
              onClick={handleDone}
            />
            <Button
              kind="plain"
              label={l10n.getString("ach-passthrough-receipt-cta")}
              testId="passthrough-receipt-make-another-payment"
              onClick={handleMakeAnotherPayment}
            />
          </div>
        </div>
      </ContentCard>
    </div>
  );
};

export default PassthroughReceiptScreen;
